import { useCallback, useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import moment from "moment-timezone";
import { Card, Col, DatePicker, Row, Tabs, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import { formatDownloadFileNameDate } from "@utils/helpers";
import memberDetailsService from "@services/memberDetails.service";
import membersService from "@services/members.service";
import { usePatientSummaryStore } from "@stores/patientSummaryStore";
import { routeMap } from "@utils/constants";
import PatientSummary from "./patientDetails";
import ProviderSummary from "./providerDetails";
import { PdfModal, PdfProvider } from "../overview/pdf";
import SummaryReport, { PdfOpenButton, SummaryReportProvider } from "./summaryReport/summaryReport";
const { RangePicker } = DatePicker;

const Summary = () => {
  const [selectedTabKey, setSelectedTabKey] = useState<string>("1");
  const patientSummaryData = usePatientSummaryStore((state) => state.patientSummaryData);
  const setPatientSummaryData = usePatientSummaryStore((state) => state.setPatientSummaryData);
  const diagnosisCodeData = usePatientSummaryStore((state) => state.diagnosisCodeData);
  const setDiagnosisCodeData = usePatientSummaryStore((state) => state.setDiagnosisCodeData);
  const providerSummaryData = usePatientSummaryStore((state) => state.providerSummaryData);
  const setProviderSummaryData = usePatientSummaryStore((state) => state.setProviderSummaryData);
  const setColumnSearchInfo = usePatientSummaryStore((state) => state.setPatientSearchText);
  const [summaryLoader, setSummaryLoader] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<any>();
  const [endDate, setEndDate] = useState<any>();
  const [prevStartDate, setPrevStartDate] = useState(startDate);
  const [prevEndDate, setPrevEndDate] = useState(endDate);
  const [providerHistoryData, setProviderHistoryData] = useState<any>([]);
  const [loadedTab, setLoadedTab] = useState({ patient: false, provider: false });
  const membersServiceGetCurrentMember = membersService?.getCurrentMember;
  const patientID = useMemo(() => membersServiceGetCurrentMember?.patient_id, [membersServiceGetCurrentMember?.patient_id]);
  const [prevPatientID, setPrevPatientID] = useState(patientID);
  const navigate = useNavigate();

  const disabledDate = (current: any) => {
    const startOfMonth = moment().startOf("month");
    const twentyFourMonthsAgo = moment().subtract(23, "months").startOf("month");
    return current && (current < twentyFourMonthsAgo || current > startOfMonth.endOf("month"));
  };

  const handleDateFilter = (range: any) => {
    setStartDate(range?.[0]?.format("YYYY-MM"));
    setEndDate(range?.[1]?.format("YYYY-MM"));
  };

  const getPatientSummaryInfo = useCallback(
    (id: number, fromDate?: any, toDate?: any) => {
      setSummaryLoader(true);
      memberDetailsService.fetchSummary(id, fromDate, toDate).subscribe({
        next: (v: any) => {
          setSummaryLoader(false);
          setPatientSummaryData(v.data.summary);
          setDiagnosisCodeData(v.data.summary.uniqueDiagnosisCodeDetails || []);
        },
        error: () => {
          setSummaryLoader(false);
          setPatientSummaryData({});
          setDiagnosisCodeData([]);
        },
      });
    },
    [setPatientSummaryData, setDiagnosisCodeData]
  );

  const getProviderSummaryInfo = useCallback(
    (id: number) => {
      setSummaryLoader(true);
      memberDetailsService.getProvidersSummary(id).subscribe({
        next: (v: any) => {
          setSummaryLoader(false);
          setProviderSummaryData(v.data.providers || []);
          setProviderHistoryData(v.data.providers || []);
        },
        error: () => {
          setSummaryLoader(false);
          setProviderSummaryData([]);
        },
      });
    },
    [setProviderSummaryData]
  );

  useEffect(() => {
    if (patientID) {
      const pId = parseInt(patientID);

      if (patientID !== prevPatientID) {
        setLoadedTab({ patient: false, provider: false });
        setPrevPatientID(patientID);
      }

      switch (selectedTabKey) {
        case "1":
          if (!loadedTab["patient"] || startDate !== prevStartDate || endDate !== prevEndDate) {
            getPatientSummaryInfo(pId, startDate, endDate);
            setLoadedTab((prev) => ({ ...prev, patient: true }));
            setPrevStartDate(startDate);
            setPrevEndDate(endDate);
            setColumnSearchInfo({});
          }
          break;
        case "2":
          if (!loadedTab["provider"]) {
            getProviderSummaryInfo(pId);
            setLoadedTab((prev) => ({ ...prev, provider: true }));
          }
          break;
        default:
          navigate(routeMap["members"]);
      }
    }
  }, [patientID, selectedTabKey, startDate, endDate, loadedTab, getPatientSummaryInfo, getProviderSummaryInfo, prevPatientID, prevStartDate, prevEndDate, navigate]);

  const isDataAvailable = providerHistoryData?.length > 0;

  const renderTabActions = () => {
    switch (selectedTabKey) {
      case "1":
        return (
          <div className="center-align-content" onClick={(event) => {event.stopPropagation()}}>
            <div className="center-align-content claims-history-btn mr--20">
              <PdfProvider title="summary-report">
                <SummaryReportProvider>
                  <PdfOpenButton title="Summary Report" />
                  <PdfModal title="Summary Report">
                    <SummaryReport patientId={patientID} />
                  </PdfModal>
                </SummaryReportProvider>
              </PdfProvider>
            </div>
            <div className="reminderDatePicker">
              <div className="reminderDateLabel">
                <span className="icon-calendar f18" />
                <span>Date Range :</span>
              </div>
              <RangePicker
                separator={<span className="range-picker-separator-members-list">-</span>}
                picker="month"
                size="middle"
                format={"YYYY-MM"}
                allowClear={true}
                dropdownClassName="reminder-picker"
                disabledDate={disabledDate}
                onChange={handleDateFilter}
                value={[startDate ? moment(startDate, "YYYY-MM") : null, endDate ? moment(endDate, "YYYY-MM") : null]}
              />
            </div>
          </div>
        );

      case "2":
        return (
          <div className="summary-wrapper__item__table__download-icon">
            <Tooltip title={isDataAvailable ? "Download XLS" : "No Data"} overlayClassName="download-tooltip">
              <div style={{ cursor: isDataAvailable ? "pointer" : "not-allowed" }}>
                {isDataAvailable ? (
                  <CSVLink
                    data={providerHistoryData?.map((data: any) => ({
                      ...data,
                      providerName: data.providerName || "-",
                      providerNPI: data.providerNPI || "-",
                      providerVendorTaxId: data.providerVendorTaxId || "-",
                      providerSpecialty: data.providerSpecialty || "-",
                      providerAddress: data.providerAddress || "-",
                      providerPhoneNumber: data.providerPhoneNumber || "-",
                    }))}
                    headers={[
                      { label: "PROVIDER NAME", key: "providerName" },
                      { label: "PROVIDER NPI", key: "providerNPI" },
                      { label: "PROVIDER VENDOR TAX ID", key: "providerVendorTaxId" },
                      { label: "PROVIDER SPECIALTY", key: "providerSpecialty" },
                      { label: "PROVIDER ADDRESS", key: "providerAddress" },
                      { label: "PROVIDER PHONE NUMBER", key: "providerPhoneNumber" },
                    ]}
                    filename={`Provider Details_${formatDownloadFileNameDate(new Date())}`}
                  >
                    <i
                      className={`btn-icon icon-download clr-n125`}
                      style={{
                        width: "20px",
                        height: "42px",
                        opacity: 1,
                      }}
                    />
                  </CSVLink>
                ) : (
                  <i
                    className={`btn-icon icon-download clr-n125 disabled`}
                    style={{
                      width: "20px",
                      height: "42px",
                      opacity: 0.5,
                    }}
                  />
                )}
              </div>
            </Tooltip>
          </div>
        );

      default:
        return null;
    }
  };

  const sortStringsWithEmptyHandling = (a: any, b: any, dataIndex: string) => {
    const aValue = a[dataIndex];
    const bValue = b[dataIndex];
  
    const aIsEmpty = aValue === null || aValue === "";
    const bIsEmpty = bValue === null || bValue === "";
  
    // For null or empty values
    if (aIsEmpty && bIsEmpty) return 0;
    if (aIsEmpty) return -1;
    if (bIsEmpty) return 1;
  
    if (dataIndex === "hccCategory") {
      const aNumber = parseInt(aValue, 10);
      const bNumber = parseInt(bValue, 10);
      return aNumber - bNumber;
    }
  
    return aValue?.localeCompare(bValue);
  };

  const columnPlaceholders = {
    hccCategory: "HCC Category",
    diagnosisCode: "Diagnosis Code",
    description: "Description",
    isV28: "Is V28?",
    icdCapturedInCurrentYear: "Repeated In Current Year",
    hccCapturedInCurrentYear: "HCC Captured In Current Year",
    providerName: "Provider Name",
    providerNPI: "Provider NPI",
    providerSpecialty: "Provider Specialty",
    providerAddress: "Provider Address",
    providerPhoneNumber: "Phone Number",
    providerVendorTaxId: "Vendor Tax Id"
  };

  return (
    <div className="summary-wrapper">
      <Card className="summary-wrapper__card" hoverable={false}>
        <div className="summary-wrapper__item">
          <Row justify="space-between" align="middle">
            <Col span={24}>
              <Tabs
                onChange={(activeKey) => setSelectedTabKey(activeKey)}
                activeKey={selectedTabKey}
                defaultActiveKey={"1"}
                type="line"
                className="summary-wrapper__item__header"
                tabBarExtraContent={<span style={{ textAlign: "right" }}>{renderTabActions()}</span>}
              >
                <Tabs.TabPane tab="Patient Details" key="1">
                  <PatientSummary
                    summaryData={patientSummaryData}
                    diagnosisCodeData={diagnosisCodeData}
                    setDiagnosisCodeData={setDiagnosisCodeData}
                    summaryLoader={summaryLoader}
                    sortStrings={sortStringsWithEmptyHandling}
                    columnPlaceholder={columnPlaceholders}
                    patientID={patientID}
                    prevPatientID={prevPatientID}
                    className="summary-wrapper__item__section"
                  />
                </Tabs.TabPane>
                <Tabs.TabPane tab="Provider Details" key="2">
                  <ProviderSummary
                    providerData={providerSummaryData}
                    providerHistoryData={providerHistoryData}
                    setProviderHistoryData={setProviderHistoryData}
                    summaryLoader={summaryLoader}
                    sortStrings={sortStringsWithEmptyHandling}
                    columnPlaceholder={columnPlaceholders}
                    patientID={patientID}
                    prevPatientID={prevPatientID}
                    className="summary-wrapper__item__section"
                  />
                </Tabs.TabPane>
              </Tabs>
            </Col>
          </Row>
        </div>
      </Card>
    </div>
  );
};

export default Summary;
